import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import OpeningCard, { TIER_LABEL } from '../components/OpeningCard';
import { openingsApi } from '../api/openingsApi';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import type { OpeningFilters, OpeningsResponse, OpeningSort } from '../../../shared/types';

/**
 * Browse the catalog. Filters live in the URL so a filtered view can be
 * bookmarked and the back button behaves the way people expect.
 */
export default function OpeningsPage() {
  const [params, setParams] = useSearchParams();

  const search = params.get('q') ?? '';
  const side = params.get('side') ?? '';
  const family = params.get('family') ?? '';
  const tier = params.get('tier') ?? '';
  const sort = (params.get('sort') as OpeningSort) || 'name';
  const page = Number(params.get('page') ?? 1);

  const [filters, setFilters] = useState<OpeningFilters | null>(null);
  const [data, setData] = useState<OpeningsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState(search);
  const debouncedSearch = useDebouncedValue(searchInput, 300);

  // Push the debounced search term into the URL, resetting to page 1 so a new
  // query never lands the user on an out-of-range page.
  useEffect(() => {
    if (debouncedSearch === search) return;
    const next = new URLSearchParams(params);
    if (debouncedSearch) next.set('q', debouncedSearch);
    else next.delete('q');
    next.delete('page');
    setParams(next, { replace: true });
    // `params`/`setParams` are stable enough here; re-running on them would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  useEffect(() => {
    openingsApi.filters().then(setFilters).catch(() => setFilters(null));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    openingsApi
      .list({
        search: search || undefined,
        sides: side ? [side] : undefined,
        families: family ? [family] : undefined,
        tiers: tier ? [tier] : undefined,
        sort,
        page,
        pageSize: 24,
      })
      .then((res) => !cancelled && setData(res))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load openings'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [search, side, family, tier, sort, page]);

  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setParams(next);
  };

  const families = useMemo(() => filters?.families ?? [], [filters]);

  return (
    <>
      <SiteHeader />
      <main className="page page--wide">
        <header className="section-head">
          <div>
            <h1>Openings</h1>
            <p className="muted">
              {data ? `${data.total} openings, each with full theory and lines to practice.` : ' '}
            </p>
          </div>
        </header>

        <div className="filter-bar">
          <input
            type="search"
            className="filter-search"
            placeholder="Search by name, ECO code or moves (try “1.e4 c5”)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search openings"
          />
          <select value={side} onChange={(e) => update('side', e.target.value)} aria-label="Side">
            <option value="">Either color</option>
            <option value="white">You play White</option>
            <option value="black">You play Black</option>
          </select>
          <select
            value={tier}
            onChange={(e) => update('tier', e.target.value)}
            aria-label="Curriculum tier"
            title="Which openings to learn next - a different question from how deep to go inside one"
          >
            <option value="">Every tier</option>
            {(filters?.tiers ?? []).map((t) => (
              <option key={t} value={t}>
                {TIER_LABEL[t] ?? t}
              </option>
            ))}
          </select>
          <select value={family} onChange={(e) => update('family', e.target.value)} aria-label="Family">
            <option value="">All families</option>
            {families.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
          <select value={sort} onChange={(e) => update('sort', e.target.value)} aria-label="Sort">
            <option value="name">Sort by name</option>
            <option value="popularity">Most common first</option>
            <option value="difficulty">Sort by difficulty</option>
            <option value="eco">Sort by ECO code</option>
            <option value="lines">Sort by number of lines</option>
          </select>
          {(search || side || family || tier) && (
            <button type="button" className="btn btn--small" onClick={() => { setSearchInput(''); setParams({}); }}>
              Clear
            </button>
          )}
        </div>

        {error && <p className="error">{error}</p>}
        {loading && !data && <p className="muted">Loading…</p>}

        {data && data.openings.length === 0 && (
          <div className="empty-card">
            <p>No openings match that search.</p>
          </div>
        )}

        <ul className="opening-grid">
          {data?.openings.map((opening) => (
            <li key={opening.id}>
              <OpeningCard opening={opening} />
            </li>
          ))}
        </ul>

        {data && data.totalPages > 1 && (
          <nav className="pager" aria-label="Pagination">
            <button
              type="button"
              className="btn btn--small"
              disabled={data.page <= 1}
              onClick={() => update('page', String(data.page - 1))}
            >
              ← Previous
            </button>
            <span className="muted">
              Page {data.page} of {data.totalPages}
            </span>
            <button
              type="button"
              className="btn btn--small"
              disabled={data.page >= data.totalPages}
              onClick={() => update('page', String(data.page + 1))}
            >
              Next →
            </button>
          </nav>
        )}
      </main>
    </>
  );
}
